import { FunctionComponent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { faCopy, faEdit } from '@fortawesome/pro-regular-svg-icons';
import { TableRow } from '../../UI/V3/Table/Row/TableRow';
import { TableCell } from '../../UI/V3/Table/Cell/TableCell';
import { ActionsDropdown } from '../../common/ActionsDropdown/ActionsDropdown';
import { ActionsDropdownOption } from '../../common/ActionsDropdown/ActionsDropdownOption';
import { useLocationsContext } from '../LocationsProvider';
import { Location } from '../../../model/location';
import { RootState } from '../../../store';
import styles from './LocationRow.module.scss';
import { formatPhoneNumber } from '@spike/phone-utils';

interface LocationRowProps {
    location: Location;
    isLast: boolean;
    isMobile: boolean;
}

const LocationRow: FunctionComponent<LocationRowProps> = ({ location, isLast }) => {
    const { handleEditLocation, handleDuplicateLocation, handleLocationClick } = useLocationsContext();

    // Get organizations from Redux state
    const { organizations } = useSelector((state: RootState) => state.organizations);

    // Get account name from organizationId
    const accountName = useMemo(() => {
        const organization = organizations.find(org => org.id === location.organizationId);
        return organization?.name || 'N/A';
    }, [organizations, location.organizationId]);

    // Format business areas array as comma-separated string
    const businessAreasText = useMemo(() => {
        if (!location.businessAreas || location.businessAreas.length === 0) return 'N/A';
        return location.businessAreas.map(area => area.charAt(0).toUpperCase() + area.slice(1)).join(', ');
    }, [location.businessAreas]);

    // Format address with suite from addresses_attributes
    const formattedAddress = useMemo(() => {
        const addressData = location.addressesAttributes?.[0];
        if (!addressData?.addressLineOne) return 'N/A';
        return addressData.addressLineTwo
            ? `${addressData.addressLineOne}, ${addressData.addressLineTwo}`
            : addressData.addressLineOne;
    }, [location.addressesAttributes]);

    const editOption: ActionsDropdownOption = {
        id: 'edit',
        label: 'Edit',
        icon: faEdit,
        onClick: () => handleEditLocation(location)
    };

    const duplicateOption: ActionsDropdownOption = {
        id: 'duplicate',
        label: 'Duplicate',
        icon: faCopy,
        onClick: () => handleDuplicateLocation(location)
    };

    const options = [editOption, duplicateOption];

    return (
        <TableRow
            className={`${styles.desktopRow} ${!location.active ? styles.inactiveRow : ''}`}
            onClick={() => handleLocationClick(location)}
        >
            <TableCell className={styles.nameCell}>
                <div className={styles.nameContent}>
                    <span className={styles.locationName}>{location.businessName}</span>
                </div>
            </TableCell>

            <TableCell>
                <span className={styles.account}>{accountName}</span>
            </TableCell>

            <TableCell>
                <span className={styles.type}>{businessAreasText}</span>
            </TableCell>

            <TableCell>
                <span
                    className={styles.address}
                    title={formattedAddress}
                >
                    {formattedAddress}
                </span>
            </TableCell>

            <TableCell>
                <span className={styles.phone}>{formatPhoneNumber(location.phone, 'US') || 'N/A'}</span>
            </TableCell>

            <TableCell>
                <span className={styles.staff}>{location.staffCount ?? 1}</span>
            </TableCell>

            <TableCell
                isActions
                onClick={event => event.stopPropagation()}
            >
                <ActionsDropdown
                    className={styles.dropdown}
                    options={options}
                    direction={isLast ? 'up' : 'down'}
                />
            </TableCell>
        </TableRow>
    );
};

export default LocationRow;
